import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import { CommunityPost, PostCategory } from '../../../database/community';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Snackbar,
    Stack,
} from '@mui/material';
import { useEffect, useState } from 'react';
import CommentEditor from '../comment/CommentEditor';
import CommunityPostModal from './PostModal';
import PostEditDialog from './PostEditDialog';
import { useApi } from '../../../api/Api';
import { useAuth } from '../../../auth/Auth';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SlideUpTransition from '../../../components/SlideUpTransition';
import { RequestSnackbar, useRequest } from '../../../api/Request';
import { LoadingButton } from '@mui/lab';
import { getConfig } from '../../../config';
import UserHeader from '../../../components/UserHeader';
import { useCache } from '../../../api/cache/Cache';
import { LikeParentType } from '../../../theSource/constants/LikeParentType';
import LikeButton from '../../../components/LikeButton';
import AudioPlayer from '../audio/AudioPlayer';
import VideoPlayer from '../video/VideoPlayer';
import { SubscriptionStatus } from '../../../database/user';
import MentionText from '../../../components/MentionText';

interface CommunityPostProps {
    post: CommunityPost;
    onSave: (post: CommunityPost) => void;
    onDelete: (post: CommunityPost) => void;
    initialOpen?: boolean;
    onModalClose?: () => void;
}
const CommunityPostCard: React.FC<CommunityPostProps> = ({
    post,
    onSave,
    onDelete,
    initialOpen,
    onModalClose,
}) => {
    const api = useApi();
    const theme = useTheme();
    const auth = useAuth();
    const user = auth.user;
    const deletePostRequest = useRequest();
    const cache = useCache();

    const [isModalOpen, setIsModalOpen] = useState<boolean>(initialOpen ?? false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showCopiedSnackbar, setShowCopiedSnackbar] = useState(false);
    const communityImagesBucket = getConfig().media.communityImagesBucket;

    const openModal = () => {
        setIsModalOpen(true);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        onModalClose?.();
    };

    const handleShare = async () => {
        const postId = `${post.owner}~${post.createdAt}`;
        const url = new URL(window.location.href);
        url.searchParams.set('post', postId);
        const shareUrl = url.toString();

        if (navigator.share) {
            try {
                await navigator.share({
                    title: `${post.ownerDisplayName}'s post`,
                    text:
                        post.content.substring(0, 100) +
                        (post.content.length > 100 ? '...' : ''),
                    url: shareUrl,
                });
                return;
            } catch (err) {
                if ((err as DOMException).name === 'AbortError') {
                    return;
                }
            }
        }

        try {
            await navigator.clipboard.writeText(shareUrl);
            setShowCopiedSnackbar(true);
        } catch {
            console.error('Failed to copy to clipboard');
        }
    };

    useEffect(() => {
        if (initialOpen) {
            setIsModalOpen(true);
        }
    }, [initialOpen]);

    const handleDelete = (post: CommunityPost) => {
        deletePostRequest.onStart();
        api.deletePost(post)
            .then((response) => {
                onDelete(response.data);
                deletePostRequest.onSuccess('Successfully deleted post');
            })
            .catch((err) => {
                console.error('Delete community post: ', err);
                deletePostRequest.onFailure(err);
            });
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
        >
            <>
                <Box
                    sx={{
                        boxShadow: `0 0 4px 4px rgba(0, 0, 0, 0.16)`,
                        borderRadius: '16px',
                        borderColor: `${theme.palette.primary.main}`,
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        transition: 'box-shadow 0.25s ease',
                        '&:hover': {
                            boxShadow: `0 0 8px 4px ${theme.palette.primary.main}50`,
                        },
                    }}
                >
                    <Dialog
                        open={showDeleteDialog}
                        onClose={
                            deletePostRequest.isLoading()
                                ? undefined
                                : () => setShowDeleteDialog(false)
                        }
                        TransitionComponent={SlideUpTransition}
                        PaperProps={{
                            sx: {
                                borderColor: 'rgba(211, 47, 47, 0.4)',
                                boxShadow:
                                    '0 24px 64px rgba(0,0,0,0.85), 0 0 0 1px rgba(211,47,47,0.2)',
                            },
                        }}
                    >
                        <RequestSnackbar request={deletePostRequest} />
                        <DialogTitle
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <WarningAmberIcon
                                sx={{ color: 'error.main', fontSize: '1.3rem' }}
                            />
                            Delete this post?
                            <IconButton
                                aria-label='close'
                                onClick={() => setShowDeleteDialog(false)}
                                sx={{ position: 'absolute', right: 10, top: 8 }}
                                disabled={deletePostRequest.isLoading()}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText
                                sx={{ color: theme.palette.secondary.main }}
                            >
                                Are you sure you want to delete this post? You can't undo
                                this action.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                color='secondary'
                                onClick={() => setShowDeleteDialog(false)}
                                disabled={deletePostRequest.isLoading()}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                color='error'
                                variant='contained'
                                onClick={() => handleDelete(post)}
                                loading={deletePostRequest.isLoading()}
                            >
                                Delete Post
                            </LoadingButton>
                        </DialogActions>
                    </Dialog>
                    <Stack direction='row' padding='10px' alignItems='center'>
                        <UserHeader
                            timestamp={post.createdAt}
                            username={post.owner}
                            displayName={cache.userSummaries.get(post.owner)?.displayName}
                            isArtist={cache.userSummaries.get(post.owner)?.isArtist}
                            subscriptionStatus={
                                cache.userSummaries.get(post.owner)?.subscriptionStatus ||
                                SubscriptionStatus.FREE_TIER
                            }
                            subscriptionOverride={
                                cache.userSummaries.get(post.owner)?.subscriptionOverride
                            }
                            isEdited={post.updatedAt !== post.createdAt}
                        />
                        {user && (user.isAdmin || user.username === post.owner) && (
                            <>
                                <IconButton
                                    onClick={() => setShowEditDialog(true)}
                                    style={{
                                        position: 'relative',
                                        top: 0,
                                        right: 0,
                                        marginLeft: 'auto',
                                        color: `${theme.palette.primary.main}`,
                                    }}
                                >
                                    <EditIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => setShowDeleteDialog(true)}
                                    style={{
                                        position: 'relative',
                                        top: 0,
                                        right: 0,
                                        color: `${theme.palette.primary.main}`,
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </>
                        )}
                        <IconButton
                            onClick={handleShare}
                            sx={{
                                color: `${theme.palette.primary.main}`,
                                ...(!user ||
                                (!user.isAdmin && user.username !== post.owner)
                                    ? { marginLeft: 'auto' }
                                    : {}),
                            }}
                        >
                            <IosShareIcon fontSize='small' />
                        </IconButton>
                    </Stack>
                    <Box sx={{ padding: '10px', paddingTop: '0px' }}>
                        <div
                            style={{
                                marginBottom: '10px',
                            }}
                        >
                            <MentionText
                                variant='body2'
                                onClick={openModal}
                                sx={{
                                    padding: '5px',
                                    paddingTop: '0px',
                                    overflowWrap: 'break-word',
                                    cursor: 'pointer',
                                }}
                            >
                                {post.content}
                            </MentionText>
                            {post.containsMedia && (
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxHeight:
                                            post.category === PostCategory.VIDEO
                                                ? 'none'
                                                : '500px',
                                        overflow:
                                            post.category === PostCategory.VIDEO
                                                ? 'visible'
                                                : 'hidden',
                                        borderRadius: '16px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        position: 'relative',
                                    }}
                                >
                                    {post.category === PostCategory.MUSIC ? (
                                        <AudioPlayer
                                            src={
                                                post.previewUrl ??
                                                `${communityImagesBucket}/${post.owner}/${post.createdAt}`
                                            }
                                        />
                                    ) : post.category === PostCategory.VIDEO ? (
                                        <VideoPlayer
                                            src={
                                                post.previewUrl ??
                                                `${communityImagesBucket}/${post.owner}/${post.createdAt}`
                                            }
                                        />
                                    ) : post.category === PostCategory.PHOTO ? (
                                        <img
                                            src={
                                                post.previewUrl ??
                                                `${communityImagesBucket}/${post.owner}/${post.createdAt}`
                                            }
                                            onClick={openModal}
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                objectFit: 'cover',
                                                cursor: 'pointer',
                                            }}
                                            alt={`${post.ownerDisplayName}'s media`}
                                        />
                                    ) : (
                                        <img
                                            src={
                                                post.previewUrl ??
                                                `${communityImagesBucket}/${post.owner}/${post.createdAt}`
                                            }
                                            onClick={openModal}
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                objectFit: 'cover',
                                                cursor: 'pointer',
                                            }}
                                            alt={`${post.ownerDisplayName}'s post`}
                                        />
                                    )}
                                </Box>
                            )}
                            <Typography
                                paddingTop={1}
                                variant='body2'
                                onClick={openModal}
                                sx={{
                                    color: theme.palette.secondary.main,
                                    opacity: 0.7,
                                    textDecoration: 'underline',
                                    textAlign: 'right',
                                    cursor: 'pointer',
                                }}
                            >
                                {post.comments.length > 0
                                    ? `${post.comments.length} comment${
                                          post.comments.length > 1 ? 's' : ''
                                      }`
                                    : 'Be the first to comment!'}
                            </Typography>
                        </div>
                        <LikeButton<CommunityPost>
                            parentType={LikeParentType.CommunityPost}
                            pk={post.owner}
                            sk={post.createdAt}
                            likes={post.likes}
                            onEdit={onSave}
                        />
                        {post.comments.length > 0 && (
                            <Box
                                key={
                                    post.comments[post.comments.length - 1].owner +
                                    post.comments[post.comments.length - 1].createdAt
                                }
                                sx={{
                                    padding: '5px',
                                    background: `${theme.palette.secondary.light}`,
                                    borderRadius: '16px',
                                }}
                            >
                                <UserHeader
                                    timestamp={
                                        post.comments[post.comments.length - 1].createdAt
                                    }
                                    username={
                                        post.comments[post.comments.length - 1].owner
                                    }
                                    displayName={
                                        cache.userSummaries.get(
                                            post.comments[post.comments.length - 1].owner,
                                        )?.displayName
                                    }
                                    isArtist={
                                        cache.userSummaries.get(
                                            post.comments[post.comments.length - 1].owner,
                                        )?.isArtist
                                    }
                                    subscriptionStatus={
                                        cache.userSummaries.get(
                                            post.comments[post.comments.length - 1].owner,
                                        )?.subscriptionStatus ||
                                        SubscriptionStatus.FREE_TIER
                                    }
                                    subscriptionOverride={
                                        cache.userSummaries.get(
                                            post.comments[post.comments.length - 1].owner,
                                        )?.subscriptionOverride
                                    }
                                />
                                <MentionText
                                    variant='body2'
                                    sx={{ overflowWrap: 'break-word', padding: '4px' }}
                                >
                                    {post.comments[post.comments.length - 1].content}
                                </MentionText>
                            </Box>
                        )}
                        <CommentEditor post={post} onSave={onSave} />
                    </Box>
                </Box>
                <Snackbar
                    open={showCopiedSnackbar}
                    autoHideDuration={2000}
                    onClose={() => setShowCopiedSnackbar(false)}
                    message='Link copied to clipboard'
                />
                <CommunityPostModal
                    post={post}
                    isModalOpen={isModalOpen}
                    handleCloseModal={handleCloseModal}
                    onSave={onSave}
                />
                {showEditDialog && (
                    <PostEditDialog
                        post={post}
                        open={showEditDialog}
                        onClose={() => setShowEditDialog(false)}
                        onSave={onSave}
                    />
                )}
            </>
        </motion.div>
    );
};

export default CommunityPostCard;
